import { AccountFormValues } from '@/pages/account/form';
import { TextInput } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import styles from './styles.module.css';

// Google Places API Types
interface Match {
	startOffset?: number;
	endOffset: number;
}

interface TextWithMatches {
	text: string;
	matches?: Match[];
}

export interface PlacePrediction {
	placeId: string;
	text: TextWithMatches;
}

// Google Places API Address Details Types
interface AddressComponent {
	longText: string;
	shortText: string;
	types: string[];
	languageCode: string;
	placeId?: string;
}

interface Location {
	latitude: number;
	longitude: number;
}

interface PostalAddress {
	regionCode: string;
	languageCode: string;
	postalCode: string;
	administrativeArea: string;
	locality: string;
	addressLines: string[];
}

export interface AddressDetails {
	formattedAddress: string;
	addressComponents: AddressComponent[];
	location: Location;
	postalAddress: PostalAddress;
}

enum AustralianState {
	NSW = 'NSW',
	QLD = 'QLD',
	SA = 'SA',
	TAS = 'TAS',
	VIC = 'VIC',
	WA = 'WA',
	ACT = 'ACT',
	NT = 'NT',
}

const GOOGLE_MAP_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? 'Invalid';
const MAPS_ENDPOINT = 'https://places.googleapis.com/v1/places:autocomplete';
const DETAILS_ENDPOINT = 'https://places.googleapis.com/v1/places/';

export const AddressField = ({
	value,
	label,
	error,
}: {
	value: string | null | undefined;
	label: string;
	error?: string;
}) => {
	const defaultValue = useRef(value);
	const previousValue = useRef(value);
	const [debouncedValue] = useDebounceValue(value, 500);
	const [results, setResults] = useState<PlacePrediction[]>([]);
	const [showResults, setShowResults] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const { setFieldValue, initialValues } = useFormikContext<AccountFormValues>();

	const handleSearch = useCallback(async () => {
		try {
			const response = await fetch(MAPS_ENDPOINT, {
				method: 'POST',
				body: JSON.stringify({
					input: debouncedValue,
					includedRegionCodes: ['au'],
					includedPrimaryTypes: ['street_address', 'geocode'],
					includeQueryPredictions: true,
				}),
				headers: {
					'X-Goog-Api-Key': GOOGLE_MAP_API_KEY,
					'Content-Type': 'application/json',
					'X-Goog-FieldMask':
						'suggestions.placePrediction.text.text,suggestions.placePrediction.placeId',
				},
			}).then(
				(res) => res.json() as Promise<{ suggestions: { placePrediction: PlacePrediction }[] }>
			);
			if (Array.isArray(response?.suggestions)) {
				setResults(response.suggestions.map((s) => s.placePrediction));
				setShowResults(true);
				setSelectedIndex(-1); // Reset selection when new results come in
			} else {
				setResults([]);
				setShowResults(false);
				setSelectedIndex(-1);
			}
		} catch {
			setResults([]);
			setShowResults(false);
			setSelectedIndex(-1);
		}
	}, [debouncedValue]);

	const fetchAddressDetails = useCallback(
		async (placeId: string) => {
			try {
				const response: AddressDetails = await fetch(DETAILS_ENDPOINT + placeId, {
					method: 'GET',
					headers: {
						'X-Goog-Api-Key': GOOGLE_MAP_API_KEY,
						'X-Goog-FieldMask': 'location,addressComponents,postalAddress,id,types',
					},
				}).then((res) => res.json());
				const { addressComponents, location, postalAddress } = response;

				setFieldValue('addressLine1', postalAddress.addressLines?.[0] ?? '');
				setFieldValue('addressLine2', postalAddress.addressLines?.[1] ?? '');
				setFieldValue(
					'council',
					addressComponents.find((c) => c.types.includes('administrative_area_level_2'))?.longText
				);
				setFieldValue('postcode', postalAddress.postalCode);
				setFieldValue(
					'state',
					addressComponents.find((c) => c.types.includes('administrative_area_level_1'))
						?.shortText as AustralianState
				);
				setFieldValue('lat', location.latitude);
				setFieldValue('lng', location.longitude);
				setFieldValue('isManualAddress', false);

				const cityComponent = addressComponents.find((c) => c.types.includes('locality'));
				if (cityComponent?.longText) {
					setFieldValue('city', cityComponent.longText);

					if (cityComponent.placeId) {
						setFieldValue('cityPlaceId', cityComponent.placeId);
					}
				} else {
					setFieldValue('city', '');
					setFieldValue('cityPlaceId', null);
				}
			} catch {
				setFieldValue('formattedAddress', '');
				setShowResults(false);
				setResults([]);
			}
		},
		[setFieldValue]
	);

	const handleSelect = useCallback(
		(prediction: PlacePrediction) => {
			setFieldValue('formattedAddress', prediction.text.text);
			setFieldValue('placeId', prediction.placeId);
			previousValue.current = prediction.text.text;
			setResults([]);
			setShowResults(false);
			setSelectedIndex(-1);
			fetchAddressDetails(prediction.placeId);
		},
		[fetchAddressDetails, setFieldValue]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (!showResults || results.length === 0) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
					break;
				case 'Enter':
					e.preventDefault();
					if (selectedIndex >= 0 && selectedIndex < results.length) {
						handleSelect(results[selectedIndex]);
					}
					break;
				case 'Escape':
					e.preventDefault();
					setShowResults(false);
					setResults([]);
					setSelectedIndex(-1);
					break;
			}
		},
		[showResults, results, selectedIndex, handleSelect]
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setFieldValue('formattedAddress', e.target.value);
			if (e.target.value === '') {
				setShowResults(false);
				setResults([]);
				setSelectedIndex(-1);
			}

			// Clear the existing address values if the user has changed address. We don't want to save an incomplete address.
			if (e.target.value !== initialValues?.formattedAddress && initialValues?.council) {
				setFieldValue('addressLine1', undefined);
				setFieldValue('addressLine2', undefined);
				setFieldValue('council', undefined);
				setFieldValue('postcode', undefined);
				setFieldValue('state', undefined);
				setFieldValue('lat', undefined);
				setFieldValue('lng', undefined);
				setFieldValue('cityPlaceId', undefined);
				setFieldValue('isManualAddress', false);
			}
		},
		[initialValues?.council, initialValues?.formattedAddress, setFieldValue]
	);

	useEffect(() => {
		if (
			debouncedValue &&
			debouncedValue !== defaultValue.current &&
			debouncedValue !== previousValue.current
		) {
			handleSearch();
		} else {
			setShowResults(false);
			setResults([]);
			setSelectedIndex(-1);
		}
	}, [handleSearch, debouncedValue]);

	return (
		<div className={styles.container}>
			<TextInput
				width="100%"
				value={value ?? ''}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				placeholder={label}
				role="combobox"
				aria-expanded={showResults}
				aria-haspopup="listbox"
				aria-autocomplete="list"
				error={error}
			/>

			{showResults && results.length > 0 && (
				<div className={styles.resultsDropdown} role="listbox">
					{results?.map((prediction, index) => (
						<button
							key={prediction.placeId}
							onClick={() => handleSelect(prediction)}
							className={`${styles.resultButton} ${
								index < results.length - 1 ? styles.resultButtonWithBorder : ''
							} ${index === selectedIndex ? styles.resultButtonSelected : ''}`}
							role="option"
							aria-selected={index === selectedIndex}
						>
							<IconMapPin strokeWidth={1} size={16} className={styles.resultIcon} />
							<div className={styles.resultText}>{prediction.text.text}</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
};
